import {
  createBrowserRouter,
  Navigate,
  Outlet,
  redirect,
  useLocation,
  useParams,
  type LoaderFunctionArgs,
  type RouteObject,
} from "react-router-dom";
import AppLayout from "./shared/AppLayout";
import RouteBoundary from "./shared/RouteBoundary";
import React, { useEffect } from "react";
import i18n from "../i18n";
import {
  detectPreferredLng,
  isSupportedLng,
  type Lng,
  useLocalePath,
} from "./localization";

export { useLocalePath };

export function localeFromI18nLanguage(
  language: string | null | undefined
): Lng | null {
  const primary = language?.toLowerCase().split(/[-_]/)[0];
  return isSupportedLng(primary) ? primary : null;
}

export function isI18nSyncedWithLocale(locale: Lng) {
  return (
    localeFromI18nLanguage(i18n.resolvedLanguage) === locale ||
    localeFromI18nLanguage(i18n.language) === locale
  );
}

export async function syncI18nLanguage(locale: Lng) {
  if (!isI18nSyncedWithLocale(locale)) {
    await i18n.changeLanguage(locale);
  }

  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
  }
}

function redirectTargetForLocale(
  request: LoaderFunctionArgs["request"],
  lng: Lng
) {
  const url = new URL(request.url);
  return `/${lng}${url.search}${url.hash}`;
}

export async function autoLocaleRedirectLoader({
  request,
}: LoaderFunctionArgs) {
  const preferred = detectPreferredLng();
  await syncI18nLanguage(preferred);

  return redirect(redirectTargetForLocale(request, preferred));
}

export async function localeLoader({ params, request }: LoaderFunctionArgs) {
  const safeLng: Lng = isSupportedLng(params.lng)
    ? params.lng
    : detectPreferredLng();

  await syncI18nLanguage(safeLng);

  if (!isSupportedLng(params.lng)) {
    return redirect(redirectTargetForLocale(request, safeLng));
  }

  return null;
}

const Home = lazyPage(() => import("../pages/Home"));
const About = lazyPage(() => import("../pages/About"));
const Cases = lazyPage(() => import("../pages/Cases"));
const Contact = lazyPage(() => import("../pages/Contact"));
const Onboard = lazyPage(() => import("../pages/Onboard"));
const NotFound = lazyPage(() => import("../pages/NotFound"));

// Services
const ServicesOverview = lazyPage(
  () => import("../pages/services/ServicesOverview")
);
const ServiceRoute = lazyPage(() => import("../pages/services/ServiceRoute"));
const IndustryLandingPage = lazyPage(
  () => import("../pages/industries/IndustryLandingPage")
);
const BookingNicheLandingPage = lazyPage(
  () => import("../pages/booking/BookingNicheLandingPage")
);

// Cases
const CaseStudy = lazyPage(() => import("../pages/cases/CaseStudyTemplate"));
const CasePageMKcosmetics = lazyPage(
  () => import("../pages/cases/CasePageMKcosmetics")
);
function lazyPage<T extends { default: React.ComponentType<any> }>(
  loader: () => Promise<T>
) {
  const Comp = React.lazy(loader);

  return {
    Component: (props: any) => {
      const { title, ...componentProps } = props;

      return (
        <RouteBoundary title={title ?? "Nalosoft"}>
          <Comp {...componentProps} />
        </RouteBoundary>
      );
    },
  };
}

/** A layout that enforces and syncs locale from the URL. */
function LocaleLayout() {
  const { lng } = useParams();
  const location = useLocation();
  const safeLng: Lng = isSupportedLng(lng) ? lng : detectPreferredLng();
  const [, rerenderAfterLocaleSync] = React.useReducer(
    (value: number) => value + 1,
    0
  );
  const isLocaleReady = isI18nSyncedWithLocale(safeLng);

  // Keep i18n + <html lang> in sync with the URL
  useEffect(() => {
    let cancelled = false;

    syncI18nLanguage(safeLng).then(() => {
      if (!cancelled) rerenderAfterLocaleSync();
    });

    return () => {
      cancelled = true;
    };
  }, [safeLng]);

  // If URL param is invalid, bounce to a supported one
  if (!isSupportedLng(lng)) {
    return (
      <Navigate
        to={{
          pathname: `/${safeLng}`,
          search: location.search,
          hash: location.hash,
        }}
        replace
      />
    );
  }

  if (!isLocaleReady) {
    return null;
  }

  return <AppLayout key={safeLng} />;
}

/** Landing on "/" -> send to "/{preferred-lng}" after i18n is ready. */
export function AutoLocaleRedirect() {
  const preferred = detectPreferredLng();
  const location = useLocation();
  const [syncedLocale, setSyncedLocale] = React.useState<Lng | null>(null);

  useEffect(() => {
    let cancelled = false;

    setSyncedLocale(null);
    syncI18nLanguage(preferred).then(() => {
      if (!cancelled) setSyncedLocale(preferred);
    });

    return () => {
      cancelled = true;
    };
  }, [preferred]);

  if (syncedLocale !== preferred) {
    return null;
  }

  return (
    <Navigate
      to={{
        pathname: `/${preferred}`,
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
}

export const routes: RouteObject[] = [
  // 1) Root -> auto-redirect to locale
  {
    path: "/",
    loader: autoLocaleRedirectLoader,
    element: <AutoLocaleRedirect />,
  },

  // 2) All site content under /:lng(en|da)
  {
    path: "/:lng",
    loader: localeLoader,
    element: <LocaleLayout />,
    children: [
      { index: true, element: <Home.Component title="Nalosoft — Home" /> },
      { path: "about", element: <About.Component title="About — Nalosoft" /> },
      {
        path: "services",
        children: [
          {
            index: true,
            element: <ServicesOverview.Component title="Services — Nalosoft" />,
          },
          {
            path: "websites",
            element: (
              <ServiceRoute.Component
                title="Websites — Nalosoft"
                service="websites"
              />
            ),
          },
          {
            path: "booking",
            element: (
              <ServiceRoute.Component
                title="Booking — Nalosoft"
                service="booking"
              />
            ),
          },
          {
            path: "webshop",
            element: (
              <ServiceRoute.Component
                title="Webshop — Nalosoft"
                service="webshop"
              />
            ),
          },
          {
            path: "mobile-apps",
            element: (
              <ServiceRoute.Component
                title="Mobile Apps — Nalosoft"
                service="mobileApps"
              />
            ),
          },
          {
            path: "custom-solutions",
            element: (
              <ServiceRoute.Component
                title="Custom Solutions — Nalosoft"
                service="customSolutions"
              />
            ),
          },
        ],
      },
      {
        path: "cases",
        // layout route: only renders <Outlet/>, keeps Header/Footer via AppLayout
        element: (
          <RouteBoundary title="Cases — Nalosoft">
            <Outlet />
          </RouteBoundary>
        ),
        children: [
          {
            index: true,
            element: <Cases.Component title="Cases — Nalosoft" />,
          },
          {
            path: "armring",
            element: (
              <CaseStudy.Component
                title="Armring Case Study — Nalosoft"
                namespace="case_armring"
              />
            ),
          },
          {
            path: "mkcosmetics",
            element: (
              <CasePageMKcosmetics.Component title="MK Cosmetics Case Study — Nalosoft" />
            ),
          },
          {
            path: "mce-it",
            element: (
              <CaseStudy.Component
                title="MCE IT Case Study — Nalosoft"
                namespace="case_mce_it"
              />
            ),
          },
          {
            path: "diagram-editor",
            element: (
              <CaseStudy.Component
                title="Diagram Editor Case Study — Nalosoft"
                namespace="case_diagram_editor"
              />
            ),
          },
        ],
      },
      {
        path: "brancher/:slug",
        element: (
          <IndustryLandingPage.Component title="Brancher - Nalosoft" />
        ),
      },
      {
        path: "booking/:niche",
        element: (
          <BookingNicheLandingPage.Component title="Bookingsystem - Nalosoft" />
        ),
      },
      {
        path: "contact",
        element: <Contact.Component title="Contact — Nalosoft" />,
      },
      {
        path: "onboard",
        element: <Onboard.Component title="Get started — Nalosoft" />,
      },
      {
        path: "schedule",
        element: <Onboard.Component title="Schedule â€” Nalosoft" />,
      },
      /* {
        path: "early-partner-program",
        element: <EarlyPartnerProgram.Component />,
      }, */

      // Not found within a given locale
      { path: "*", element: <NotFound.Component title="404 — Nalosoft" /> },
    ],
  },

  // 3) Any other path (e.g. random top-level) -> send to preferred locale
  {
    path: "*",
    loader: autoLocaleRedirectLoader,
    element: <AutoLocaleRedirect />,
  },
];

export function createAppRouter() {
  return createBrowserRouter(routes);
}
